import { useEffect, useState } from "react"

import { ContentObject, ContentObjectItem, SupportedEmbeddingTypes, VectorSearchQuery } from "@vertesia/common"

import { EmbeddingsTypeButtonGroup } from "@/components/buttons/EmbeddingsTypeButtonGroup"
import { EmbeddingsDataTable } from "@/components/datatables/EmbeddingsDataTable"
import { useUserSession } from "@session/UserSession"

interface EmbeddingsPanelProps {
    document: ContentObjectItem
    fullHeight?: boolean
}
export function EmbeddingsPanel({ document, fullHeight = false }: Readonly<EmbeddingsPanelProps>) {
    const { client, isLoading } = useUserSession()
    const [embeddingDocument, setEmbeddingDocument] = useState<ContentObject | undefined>(undefined)
    const [embeddingsType, setEmbeddingsType] = useState<SupportedEmbeddingTypes | undefined>(undefined)
    const [embeddingDocuments, setEmbeddingDocuments] = useState<ContentObjectItem[]>([])

    useEffect(() => {
        if (!client || isLoading) {
            return
        }

        client.objects.retrieve(document.id, "+embeddings").then((doc) => {
            if (doc?.embeddings === undefined) {
                setEmbeddingsType(undefined)
                setEmbeddingDocument(undefined)
                return
            }

            setEmbeddingDocument(doc)
            const determineEmbeddingType = () => {
                if (doc.embeddings.text) {
                    return SupportedEmbeddingTypes.text
                } else if (doc.embeddings.image) {
                    return SupportedEmbeddingTypes.image
                }
                return SupportedEmbeddingTypes.properties
            }
            setEmbeddingsType(determineEmbeddingType())
        })
    }, [client, isLoading, document])

    useEffect(() => {
        if (embeddingsType) {
            searchEmbeddings()
        }
    }, [embeddingsType])

    const searchEmbeddings = async () => {
        const vector: VectorSearchQuery = {
            objectId: document.id,
            config: {
                text: embeddingsType === SupportedEmbeddingTypes.text,
                image: embeddingsType === SupportedEmbeddingTypes.image,
                properties: embeddingsType === SupportedEmbeddingTypes.properties
            }
        }

        const query = { vector } as any

        client.objects.search({ limit: 200, offset: 0, query }).then((response) => {
            setEmbeddingDocuments(response.results)
        })
    }

    return (
        <>
            {
                !embeddingsType &&
                <div className="flex w-full justify-center p-2 text-gray-500">
                    No similar content available for this document.
                </div>
            }
            {
                embeddingsType &&
                <div className="flex flex-col gap-4 px-4 pb-2">
                    <div className='flex flex-row w-full items-end justify-between'>
                        <div className="h-5 capitalize">{embeddingsType} embeddings</div>
                        <EmbeddingsTypeButtonGroup document={embeddingDocument!} type={embeddingsType} onUpdate={setEmbeddingsType} />
                    </div>
                    <EmbeddingsDataTable data={embeddingDocuments} fullHeight={fullHeight} />
                </div>
            }
        </>
    )
}